import { useId, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { formatWorkspaceTime } from '@/lib/formatCentralTime'
import { useConfirm } from '@/components/ui/ConfirmDialog'
import { useAuthStore } from '@/stores/authStore'

export interface DealNote {
  id: string
  body: string
  createdAt: string
  updatedAt: string
  authorName: string | null
  authorUserId?: string | null
}
export interface DealNotesData { notes: DealNote[]; hasMore: boolean }
export interface NoteActor { userId: string; role: string }
export interface DealNotesTransport {
  list: (dealId: string, before?: string) => Promise<DealNotesData>
  add: (dealId: string, body: string) => Promise<DealNote>
  update: (dealId: string, noteId: string, body: string) => Promise<DealNote>
  remove: (dealId: string, noteId: string) => Promise<void>
}
const base = (id: string) => `/api/deals/${encodeURIComponent(id)}/notes`
const liveTransport: DealNotesTransport = {
  list: async (id, before) => (await apiClient.get<{ data: DealNotesData }>(base(id), before ? { params: { before } } : undefined)).data.data,
  add: async (id, body) => (await apiClient.post<{ data: DealNote }>(base(id), { body })).data.data,
  update: async (id, nid, body) => (await apiClient.patch<{ data: DealNote }>(`${base(id)}/${encodeURIComponent(nid)}`, { body })).data.data,
  remove: async (id, nid) => { await apiClient.delete(`${base(id)}/${encodeURIComponent(nid)}`) },
}

/** Newest first; ties broken by id desc (matches the server order). */
export function sortNotesNewestFirst(notes: DealNote[]): DealNote[] {
  return [...notes].sort((a, b) => {
    const d = Date.parse(b.createdAt) - Date.parse(a.createdAt)
    return d !== 0 ? d : b.id < a.id ? -1 : b.id > a.id ? 1 : 0
  })
}

export function canManageNote(actor: NoteActor | null, note: DealNote, canEdit: boolean): boolean {
  if (!canEdit || !actor) return false
  if (note.authorUserId && note.authorUserId === actor.userId) return true
  const role = actor.role.toUpperCase()
  return role === 'ADMIN' || role === 'OWNER'
}

export function DealNotes({ dealId, canEdit, transport = liveTransport, actor }: {
  dealId: string
  canEdit: boolean
  transport?: DealNotesTransport
  /** Who is reading. Omit to use the signed-in user. */
  actor?: NoteActor | null
}) {
  return <Notes key={dealId} dealId={dealId} canEdit={canEdit} transport={transport} actor={actor} />
}

function Notes({ dealId, canEdit, transport, actor }: {
  dealId: string
  canEdit: boolean
  transport: DealNotesTransport
  actor: NoteActor | null | undefined
}) {
  const session = useAuthStore((s) => s.user)
  const { confirm } = useConfirm()
  const who: NoteActor | null = actor !== undefined
    ? actor
    : session ? { userId: session.id, role: session.role } : null
  const client = useQueryClient()
  const labelId = useId()
  const inputId = useId()
  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [older, setOlder] = useState<DealNote[]>([])
  const [olderHasMore, setOlderHasMore] = useState(false)
  const [olderError, setOlderError] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const queryKey = ['deal-notes', dealId]
  const query = useQuery({ queryKey, queryFn: () => transport.list(dealId), retry: false })
  const refresh = () => void client.invalidateQueries({ queryKey })

  const add = useMutation({
    mutationFn: (body: string) => transport.add(dealId, body),
    retry: false,
    onSuccess: (note) => {
      client.setQueryData<DealNotesData>(queryKey, (old) => ({
        notes: sortNotesNewestFirst([note, ...(old?.notes ?? []).filter((n) => n.id !== note.id)]),
        hasMore: Boolean(old?.hasMore),
      }))
      setDraft('')
      refresh()
    },
  })
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => transport.update(dealId, id, body),
    retry: false,
    onSuccess: (note) => {
      client.setQueryData<DealNotesData>(queryKey, (old) => old && ({ ...old, notes: old.notes.map((n) => n.id === note.id ? note : n) }))
      setEditingId(null)
      refresh()
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => transport.remove(dealId, id),
    retry: false,
    onSuccess: (_v, id) => {
      client.setQueryData<DealNotesData>(queryKey, (old) => old && ({ ...old, notes: old.notes.filter((n) => n.id !== id) }))
      refresh()
    },
  })

  const trimmed = draft.trim()
  const submit = () => { if (trimmed && !add.isPending) add.mutate(trimmed) }
  const visible = (() => {
    const seen = new Set<string>()
    const out: DealNote[] = []
    for (const note of [...(query.data?.notes ?? []), ...older]) {
      if (seen.has(note.id)) continue
      seen.add(note.id)
      out.push(note)
    }
    return sortNotesNewestFirst(out)
  })()
  const hasMore = older.length > 0 ? olderHasMore : Boolean(query.data?.hasMore)
  const loadOlder = async () => {
    const last = visible[visible.length - 1]
    if (!last || loadingOlder) return
    setLoadingOlder(true)
    setOlderError(false)
    try {
      const page = await transport.list(dealId, `${last.createdAt},${last.id}`)
      setOlder((prev) => {
        const seen = new Set([...(query.data?.notes ?? []), ...prev].map((n) => n.id))
        return [...prev, ...page.notes.filter((n) => !seen.has(n.id))]
      })
      setOlderHasMore(page.hasMore)
    } catch {
      setOlderError(true)
    } finally {
      setLoadingOlder(false)
    }
  }

  return <section className="ds-tile-inset p-4 space-y-3" aria-labelledby={labelId} data-testid="deal-notes">
    <h3 id={labelId} className="font-semibold">Notes</h3>
    {canEdit && <form data-testid="deal-note-form" className="space-y-2" onSubmit={(e) => { e.preventDefault(); submit() }}>
      <label htmlFor={inputId} className="sr-only">New note</label>
      <textarea id={inputId} value={draft} maxLength={10000} rows={3} placeholder="Add a note…"
        className="w-full rounded border border-[var(--border)] bg-transparent p-2"
        onChange={(e) => setDraft(e.target.value)} />
      {add.isError && <p role="alert">Note could not be saved. Your text is still here.</p>}
      <Button type="submit" size="sm" disabled={!trimmed || add.isPending} loading={add.isPending}>Add note</Button>
    </form>}
    {query.isPending ? <p role="status">Loading notes…</p> : query.isError ? (
      <div role="alert"><p>Notes could not be loaded.</p>
        <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>Retry notes</Button></div>
    ) : query.data.notes.length === 0 ? <p className="text-sm text-midnight-text3">No notes yet.</p> : (
      <ul className="space-y-3" aria-label="Lead notes" data-testid="deal-notes-list">
        {visible.map((note) => {
          const edited = Date.parse(note.updatedAt) - Date.parse(note.createdAt) > 1000
          return <li key={note.id} className="border-b border-[var(--border)] pb-3">
            <NoteMeta
              note={note}
              edited={edited}
              showActions={canManageNote(who, note, canEdit) && editingId !== note.id}
              pending={remove.isPending}
              onEdit={() => { setEditingId(note.id); setEditDraft(note.body) }}
              onDelete={() => {
                void confirm({
                  title: 'Delete this note?',
                  impact: 'The note is removed. The record stays.',
                  confirmLabel: 'Delete note',
                  tone: 'danger',
                }).then((ok) => { if (ok) remove.mutate(note.id) })
              }}
            />
            {editingId === note.id ? <div className="space-y-2 mt-1">
              <label className="sr-only" htmlFor={`edit-${note.id}`}>Edit note</label>
              <textarea id={`edit-${note.id}`} value={editDraft} maxLength={10000} rows={3}
                className="w-full rounded border border-[var(--border)] bg-transparent p-2"
                onChange={(e) => setEditDraft(e.target.value)} />
              {update.isError && <p role="alert">Edit could not be saved.</p>}
              <div className="flex gap-2">
                <Button size="sm" disabled={!editDraft.trim() || update.isPending}
                  onClick={() => update.mutate({ id: note.id, body: editDraft.trim() })}>Save</Button>
                <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
              </div>
            </div> : <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] mt-1">{note.body}</p>}
          </li>
        })}
      </ul>
    )}
    {query.data && hasMore && (
      <Button type="button" variant="secondary" size="sm" onClick={() => void loadOlder()} disabled={loadingOlder} loading={loadingOlder}>
        Load older notes
      </Button>
    )}
    {olderError && <p role="alert">Older notes could not be loaded. Tap Load older notes to try again.</p>}
  </section>
}

/**
 * The time · author line, plus one 44×44 actions control. The menu opens in the
 * flow under that line (Edit, then Delete) so it is never clipped by the drawer.
 * The button's negative margin gives the 44px target back, so the line stays put.
 */
function NoteMeta({ note, edited, showActions, pending, onEdit, onDelete }: {
  note: DealNote
  edited: boolean
  showActions: boolean
  pending: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center">
      <p className="min-w-0 text-sm text-midnight-text3">
        <time dateTime={note.createdAt}>{formatWorkspaceTime(note.createdAt)}</time>
        {note.authorName ? ` · ${note.authorName}` : ''}{edited ? ' · edited' : ''}
      </p>
      {showActions && (
        <button
          type="button"
          aria-label="Note actions"
          aria-expanded={open}
          aria-haspopup="menu"
          className="inline-flex items-center justify-center rounded-full text-base leading-none"
          style={{ width: 44, height: 44, margin: '-12px 0' }}
          onClick={() => setOpen((v) => !v)}
        >
          <MoreHorizontal aria-hidden className="h-5 w-5" />
        </button>
      )}
      {showActions && open && (
        <div role="menu" aria-label="Note actions" className="col-span-2 flex flex-wrap gap-2 pt-1">
          <button type="button" role="menuitem" className="btn-secondary min-h-11 px-3" onClick={() => { setOpen(false); onEdit() }}>Edit</button>
          <button type="button" role="menuitem" className="btn-secondary min-h-11 px-3" disabled={pending} onClick={() => { setOpen(false); onDelete() }}>Delete</button>
        </div>
      )}
    </div>
  )
}
